import { equalNumbers, hmacSha256, result, sha256 } from "./core.js";
import type { VerificationCheck, VerificationResult } from "./core.js";

export type PlinkoRisk = "low" | "medium" | "high";

const HALF: Record<PlinkoRisk, Record<number, readonly string[]>> = {
  low: {
    8: ["5.6", "2.1", "1.1", "1", "0.5"], 9: ["5.6", "2", "1.6", "1", "0.7"],
    10: ["8.9", "3", "1.4", "1.1", "1", "0.5"], 11: ["8.4", "3", "1.9", "1.3", "1", "0.7"],
    12: ["10", "3", "1.6", "1.4", "1.1", "1", "0.5"], 13: ["8.1", "4", "3", "1.9", "1.2", "0.9", "0.7"],
    14: ["7.1", "4", "1.9", "1.4", "1.3", "1.1", "1", "0.5"], 15: ["15", "8", "3", "2", "1.5", "1.1", "1", "0.7"],
    16: ["16", "9", "2", "1.4", "1.4", "1.2", "1.1", "1", "0.5"]
  },
  medium: {
    8: ["13", "3", "1.3", "0.7", "0.4"], 9: ["18", "4", "1.7", "0.9", "0.5"],
    10: ["22", "5", "2", "1.4", "0.6", "0.4"], 11: ["24", "6", "3", "1.8", "0.7", "0.5"],
    12: ["33", "11", "4", "2", "1.1", "0.6", "0.3"], 13: ["43", "13", "6", "3", "1.3", "0.7", "0.4"],
    14: ["58", "15", "7", "4", "1.9", "1", "0.5", "0.2"], 15: ["88", "18", "11", "5", "3", "1.3", "0.5", "0.3"],
    16: ["110", "41", "10", "5", "3", "1.5", "1", "0.5", "0.3"]
  },
  high: {
    8: ["29", "4", "1.5", "0.3", "0.2"], 9: ["43", "7", "2", "0.6", "0.2"],
    10: ["76", "10", "3", "0.9", "0.3", "0.2"], 11: ["120", "14", "5.2", "1.4", "0.4", "0.2"],
    12: ["170", "24", "8.1", "2", "0.7", "0.2", "0.2"], 13: ["260", "37", "11", "4", "1", "0.2", "0.2"],
    14: ["420", "56", "18", "5", "1.9", "0.3", "0.2", "0.2"], 15: ["620", "83", "27", "8", "3", "0.5", "0.2", "0.2"],
    16: ["1000", "130", "26", "9", "4", "2", "0.2", "0.2", "0.2"]
  }
};

function multipliers(risk: PlinkoRisk, rows: number): readonly string[] {
  const half = HALF[risk][rows];
  if (!half) throw new Error("INVALID_CONFIGURATION");
  const tail = (rows + 1) % 2 ? half.slice(0, -1) : half.slice();
  return [...half, ...tail.reverse()];
}

function payoutUnits(wagerUnits: number, multiplier: string): number {
  if (!Number.isSafeInteger(wagerUnits) || wagerUnits < 0) throw new Error("INVALID_WAGER");
  const [whole, fraction = ""] = multiplier.split(".");
  const denominator = 10n ** BigInt(fraction.length);
  const numerator = BigInt(`${whole}${fraction}`);
  return Number((BigInt(wagerUnits) * numerator) / denominator);
}

export async function calculatePlinkoOutcome(input: {
  serverSeed: string;
  roundId: string;
  clientSeed: string;
  nonce: number;
  risk: PlinkoRisk;
  rows: number;
}): Promise<{ path: (0 | 1)[]; slot: number; multiplier: string }> {
  if (!Number.isInteger(input.rows) || input.rows < 8 || input.rows > 16) throw new Error("INVALID_ROWS");
  const message = ["plinko", input.roundId, input.clientSeed, input.nonce, 0].join(":");
  const bytes = await hmacSha256(input.serverSeed, message);
  const path = Array.from({ length: input.rows }, (_, row) => (bytes[row]! & 1) as 0 | 1);
  const slot = path.reduce<number>((sum, direction) => sum + direction, 0);
  return { path, slot, multiplier: multipliers(input.risk, input.rows)[slot]! };
}

export async function verifyPlinko(input: {
  serverSeed: string;
  serverSeedHash: string;
  roundId: string;
  clientSeed: string;
  nonce: number;
  risk: PlinkoRisk;
  rows: number;
  wagerUnits: number;
  recordedPath: readonly number[];
  recordedSlot: number;
  recordedMultiplier: string;
  recordedPayoutUnits: number;
}): Promise<VerificationResult<{
  serverSeedHash: string;
  path: (0 | 1)[];
  slot: number;
  multiplier: string;
  payoutUnits: number;
}>> {
  const serverSeedHash = await sha256(input.serverSeed);
  const outcome = await calculatePlinkoOutcome(input);
  const calculatedPayout = payoutUnits(input.wagerUnits, outcome.multiplier);
  const checks: VerificationCheck[] = [
    { key: "commitment", ok: serverSeedHash === input.serverSeedHash, expected: input.serverSeedHash, actual: serverSeedHash },
    {
      key: "outcome",
      ok: equalNumbers(outcome.path, input.recordedPath)
        && outcome.slot === input.recordedSlot
        && outcome.multiplier === input.recordedMultiplier,
      expected: { path: [...input.recordedPath], slot: input.recordedSlot, multiplier: input.recordedMultiplier },
      actual: outcome
    },
    { key: "payout", ok: calculatedPayout === input.recordedPayoutUnits, expected: input.recordedPayoutUnits, actual: calculatedPayout }
  ];
  return result({ serverSeedHash, ...outcome, payoutUnits: calculatedPayout }, checks);
}

